
import {getEditableTexts} from "../common/get-editable-texts";

const getUsedMergeFields = async (availableMergeFields, ccEditor) => {
    if (!availableMergeFields?.length || !ccEditor?.editor) return [];

    // load all text areas used on the product
    const product = await ccEditor.editor.getProduct();
    const productModel = await product.getProductModel();
    const textAreas = getEditableTexts({items: await productModel.getAllItems()});
    if (!textAreas?.length) return [];

    // evaluate text areas to see where merge fields have been used
    let usedMergeFields = [];
    for (const textArea of textAreas) {
        for (const mergeField of availableMergeFields) {
            if (!textArea.text.includes(mergeField.mergeField)) continue;

            usedMergeFields.push({
                surfaceName: textArea.surfaceName,
                tagName: mergeField.mergeField,
                type: mergeField.type,
                defaultValue: mergeField.defaultValuePrint,
                dataSource: mergeField.dataSource,
                defaultValueProof: mergeField.defaultValueProof
            });

        }
    }

    return usedMergeFields;

};

const getInvalidMergeFieldsFromEditor = async (availableMergeFields, ccEditor) => {
    if (!availableMergeFields?.length || !ccEditor?.editor) return [];

    // load all text areas used on the product
    const product = await ccEditor.editor.getProduct();
    const productModel = await product.getProductModel();
    const textAreas = getEditableTexts({items: await productModel.getAllItems()});
    if (!textAreas?.length) return [];

    // evaluate text areas to see where merge fields have been used
    let invalidMergeFields = [];
    for (const textArea of textAreas) {
        if (!textArea.text) continue;

        invalidMergeFields = invalidMergeFields.concat(
            getInvalidMergeFieldsFromText(availableMergeFields, textArea.text, textArea.name, textArea.surfaceName)
        );

    }

    return invalidMergeFields;

};

const getInvalidMergeFieldsFromText = (availableMergeFields, text, fieldName = "", surfaceName = "") => {

    let invalidMergeFields = [];
    if (!text || !availableMergeFields?.length) return invalidMergeFields;

    // find the length of the longest merge field - will be used for ignoring long mismatches
    const maxMergeFieldLength = availableMergeFields.sort((x, y) => x.mergeField.length < y.mergeField.length)[0].mergeField.length;

    // check for unknown merge fields
    let textAreaMergeFields = text.match(/{{([^}]*)}}/g); // build array of all strings that start and end with double curly braces
    if (textAreaMergeFields !== null) {
        for (const textAreaMergeField of textAreaMergeFields) {
            if (!availableMergeFields.find(x => x.mergeField === textAreaMergeField) // if merge field not in the list of known merge fields
                && textAreaMergeField.length <= maxMergeFieldLength+10 // and the string isn't longer than would be expected for an intentional merge field addition
                && !invalidMergeFields.map(x => x.tag).includes(textAreaMergeField) // and the merge field has not already been included in results
            ) {
                invalidMergeFields.push({
                    tag: textAreaMergeField,
                    reason: "Tag unrecognized",
                    reportErrorRealtime: true,
                    fieldName,
                    surfaceName
                });
            }
        }
    }

    // check for potential merge fields with missing brace
    textAreaMergeFields = text.match(/{([^\s]*)}/g); // build array of all strings that start and end with a single curly brace
    if (textAreaMergeFields !== null) {
        for (const textAreaMergeField of textAreaMergeFields) {
            if ((!textAreaMergeField.startsWith("{{") || !textAreaMergeField.endsWith("}}")) // if merge field is not wrapped in double-curly braces
                && !invalidMergeFields.map(x => x.tag).includes(textAreaMergeField) // and the merge field has not already been included in results
            ) {
                invalidMergeFields.push({
                    tag: textAreaMergeField,
                    reason: "Tag incomplete",
                    reportErrorRealtime: true,
                    fieldName,
                    surfaceName
                });
            }
        }
    }

    // check for any known merge fields that are missing all braces
    for (const availableMergeField of availableMergeFields) {
        const checkMergeField = availableMergeField.mergeField.replace(/[{}]/g, ""); // remove braces from the known merge field

        // check if it is referenced in content
        if (text.indexOf(checkMergeField) < 0) continue;

        // find occurrences where missing braces and is not contained within a word
        if (new RegExp(`[^{a-zA-Z]${checkMergeField}[^}'’a-zA-Z]`).test(" "+text+" ")) {
            invalidMergeFields.push({
                tag: checkMergeField,
                reason: "Tag missing braces",
                reportErrorRealtime: checkMergeField.indexOf(".") >= 0, // only report if a namespaced merge field - will still be reported upon save
                fieldName,
                surfaceName
            });
        }
    }

    return invalidMergeFields;

};

export {
    getUsedMergeFields,
    getInvalidMergeFieldsFromEditor,
    getInvalidMergeFieldsFromText
};
